import React from "react";
import { motion } from "framer-motion";
import { Activity, ChevronRight, CircleOff } from "lucide-react";
import { useTranslate } from "../../i18n/useTranslate";

/* ──────────────────────────────────────────────────────────────────────────
 * Shared primitives for the live engine sections.
 *
 * Every existing page in this product was built around hardcoded demo
 * figures. These components are how the §1–§22 engines get onto those pages
 * without rewriting them: a live section is inserted at the top, the static
 * content stays below, and the two are visually distinguishable so nobody
 * mistakes one for the other.
 *
 * That distinction is the whole reason these exist. A page showing a
 * measured figure next to an invented one, in the same typography, is worse
 * than a page showing only invented ones — because the reader has no way to
 * tell which is which and will assume the wrong thing about both.
 * ────────────────────────────────────────────────────────────────────────── */

/* The band that marks everything below it as engine output. */
export function LiveSection({ title, section, children, note }) {
  const t = useTranslate();
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-2xl border-2 border-indigo-200 bg-white shadow-sm"
    >
      <header className="flex items-center gap-2 border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-white px-4 py-2.5">
        <Activity size={14} className="text-indigo-600" />
        <h3 className="text-xs font-black uppercase tracking-wider text-indigo-900">
          {t(title)}
        </h3>
        {section && (
          <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[9px] font-black text-indigo-700">
            {section}
          </span>
        )}
        <span className="ml-auto rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-black text-emerald-700">
          {t("LIVE ENGINE")}
        </span>
      </header>
      <div className="p-4">{children}</div>
      {note && (
        <p className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] leading-snug text-slate-500">
          {note}
        </p>
      )}
    </motion.section>
  );
}

/* A figure that came from an engine. `unknown` renders the honest null
 * rather than a dash the reader will interpret as zero. */
export function Stat({ label, value, unit, unknown, reason, tone = "slate" }) {
  const t = useTranslate();
  const TONE = {
    slate: "text-slate-900",
    red: "text-red-700",
    amber: "text-amber-700",
    green: "text-emerald-700",
    indigo: "text-indigo-700",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {t(label)}
      </div>
      {unknown ? (
        <>
          <div className="flex items-center gap-1 text-[13px] font-black text-slate-400">
            <CircleOff size={12} />
            {t("Not measurable")}
          </div>
          {reason && (
            <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{reason}</p>
          )}
        </>
      ) : (
        <div className={`text-xl font-black tabular-nums ${TONE[tone]}`}>
          {value}
          {unit && <span className="ml-1 text-[11px] font-bold text-slate-400">{unit}</span>}
        </div>
      )}
    </div>
  );
}

/* Ordered by severity by the caller; this just renders one. */
export function Finding({ severity, area, text }) {
  const SEV = {
    critical: "border-red-300 bg-red-50 text-red-900",
    high: "border-orange-300 bg-orange-50 text-orange-900",
    medium: "border-amber-200 bg-amber-50 text-amber-900",
    low: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${SEV[severity] ?? SEV.low}`}>
      <div className="text-[9px] font-black uppercase tracking-wider opacity-70">
        {severity}{area ? ` · ${area}` : ""}
      </div>
      <p className="text-[11px] leading-snug">{text}</p>
    </div>
  );
}

/* Collapsible assumptions. Every engine publishes them; every section shows
 * them, because a figure whose assumptions are hidden is a figure nobody
 * can argue with. */
export function Assumptions({ items, label = "Method and assumptions" }) {
  const t = useTranslate();
  if (!items?.length) return null;
  return (
    <details className="mt-3">
      <summary className="flex cursor-pointer items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-slate-600">
        <ChevronRight size={10} />
        {t(label)}
      </summary>
      <ul className="mt-1.5 space-y-1">
        {items.map((a) => (
          <li key={a} className="text-[10px] leading-snug text-slate-500">· {a}</li>
        ))}
      </ul>
    </details>
  );
}

/* Marks the static demo content that follows a live section, so the two are
 * never confused. */
export function StaticContentDivider({ label = "Illustrative demo content below" }) {
  const t = useTranslate();
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="h-px flex-1 bg-slate-200" />
      <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-slate-500">
        {t(label)}
      </span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}
